import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Tabs } from '../components/ui/tabs';
import { StatCard } from '../components/ui/bento-grid';
import { CardSpotlight } from '../components/ui/card-spotlight';
import { StatefulButton } from '../components/ui/stateful-button';
import {
  Search,
  Link2,
  Globe,
  AlertTriangle,
  RefreshCw,
  Plus,
  ExternalLink,
  Trash2,
  Download,
  Upload,
  CheckCircle,
  XCircle,
  Copy,
  FileText,
  Unlink,
  LinkIcon,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  Hash,
  StickyNote,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  BarChart3,
  MapPin,
  Monitor,
  Smartphone,
  Tablet,
  Info,
  Shield,
} from 'lucide-react';
import {
  getGSCStatus,
  getAuthUrl,
  disconnectGSC,
  listSites,
  getPerformance,
  getKeywords,
  getPageTrend,
  inspectUrlApi,
  batchInspect,
  getInspectionResults,
  getInspectionBudget,
  getSitemaps,
  submitSitemap,
  deleteSitemap,
  discoverUntracked,
  getDisavowSuggestions,
  generateDisavow,
  linkSiteToProject,
  unlinkSite,
  importDiscovered,
  getDisavowHistory,
} from '../api/gsc';
import { fetchProjects } from '../api/projects';

// Default date range: last 3 months
function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

export default function SearchConsole() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Check if just connected or error from OAuth callback
  useEffect(() => {
    if (searchParams.get('connected') === 'true') {
      toast.success('Successfully connected to Google Search Console!');
      queryClient.invalidateQueries(['gsc-status']);
      queryClient.invalidateQueries(['gsc-sites']);
      setSearchParams({});
    }
    const gscError = searchParams.get('gsc_error');
    if (gscError) {
      toast.error(`GSC connection failed: ${gscError}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient]);

  // GSC status query
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['gsc-status'],
    queryFn: getGSCStatus,
  });

  // Sites query (only if connected)
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['gsc-sites'],
    queryFn: listSites,
    enabled: statusData?.connected,
  });

  // Projects for linking
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: getAuthUrl,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to get auth URL');
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: disconnectGSC,
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-status']);
      queryClient.invalidateQueries(['gsc-sites']);
      toast.success('Disconnected from Google Search Console');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to disconnect');
    },
  });

  const isConnected = statusData?.connected;
  const sites = sitesData?.sites || [];
  const projects = projectsData?.projects || [];

  const tabs = [
    {
      title: 'Overview',
      value: 'overview',
      icon: <Globe className="h-4 w-4" />,
      content: (
        <OverviewTab
          isConnected={isConnected}
          sites={sites}
          projects={projects}
          onConnect={() => connectMutation.mutate()}
          onDisconnect={() => disconnectMutation.mutate()}
          isConnecting={connectMutation.isPending}
          isDisconnecting={disconnectMutation.isPending}
          selectedSite={selectedSite}
          setSelectedSite={setSelectedSite}
        />
      ),
    },
    {
      title: 'Keywords',
      value: 'keywords',
      icon: <Search className="h-4 w-4" />,
      content: (
        <KeywordsTab
          isConnected={isConnected}
          selectedSite={selectedSite}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ),
    },
    {
      title: 'Pages',
      value: 'pages',
      icon: <BarChart3 className="h-4 w-4" />,
      content: (
        <PerformanceTab
          isConnected={isConnected}
          selectedSite={selectedSite}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      ),
    },
    {
      title: 'Index Status',
      value: 'index-status',
      icon: <CheckCircle className="h-4 w-4" />,
      content: (
        <UrlInspectionTab
          isConnected={isConnected}
          selectedSite={selectedSite}
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
        />
      ),
    },
    {
      title: 'Sitemaps',
      value: 'sitemaps',
      icon: <FileText className="h-4 w-4" />,
      content: (
        <SitemapsTab
          isConnected={isConnected}
          selectedSite={selectedSite}
        />
      ),
    },
    {
      title: 'Untracked',
      value: 'untracked',
      icon: <Link2 className="h-4 w-4" />,
      content: (
        <UntrackedTab
          isConnected={isConnected}
          selectedSite={selectedSite}
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
        />
      ),
    },
    {
      title: 'Disavow',
      value: 'disavow',
      icon: <AlertTriangle className="h-4 w-4" />,
      content: (
        <DisavowTab
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Google Search Console</h1>
          <p className="text-muted-foreground mt-1">
            Keywords, pages, index status, sitemaps, and SEO insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isConnected && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2.5 py-1.5 rounded-lg">
              <Info className="h-3.5 w-3.5" />
              Data is 2-3 days behind
            </div>
          )}
          {!statusData?.configured && (
            <div className="text-sm text-warning bg-warning/10 px-3 py-1.5 rounded-lg">
              GSC not configured in backend
            </div>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared: NotConnected / NoSite placeholders
// ---------------------------------------------------------------------------
function NotConnectedMsg({ msg }) {
  return (
    <div className="text-center py-16">
      <p className="text-muted-foreground">{msg || 'Connect Google Search Console first'}</p>
    </div>
  );
}

function NoSiteMsg() {
  return (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Select a site from the Overview tab first</p>
    </div>
  );
}

// Shared: Date range picker
function DateRangePicker({ dateRange, setDateRange }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={dateRange.startDate}
        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
        className="px-2 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm"
      />
      <span className="text-muted-foreground text-sm">to</span>
      <input
        type="date"
        value={dateRange.endDate}
        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
        className="px-2 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm"
      />
    </div>
  );
}

// Shared: Loading skeleton rows
function TableSkeleton({ cols = 5, rows = 8 }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 bg-muted rounded w-3/4" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

// Shared: CSV export helper
function exportToCsv(data, columns, filename) {
  const header = columns.map(c => c.label).join(',');
  const rows = data.map(row => columns.map(c => {
    const val = c.accessor(row);
    return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
  }).join(','));
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// KeywordsTab
// ---------------------------------------------------------------------------
function KeywordsTab({ isConnected, selectedSite, dateRange, setDateRange }) {
  const [startRow, setStartRow] = useState(0);
  const [queryFilter, setQueryFilter] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [device, setDevice] = useState('');
  const [country, setCountry] = useState('');
  const [sortBy, setSortBy] = useState('clicks');
  const [sortDir, setSortDir] = useState('desc');
  const ROW_LIMIT = 1000;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['gsc-keywords', selectedSite, dateRange, startRow, queryFilter, pageFilter, device, country],
    queryFn: () => getKeywords(selectedSite, {
      ...dateRange,
      startRow,
      rowLimit: ROW_LIMIT,
      queryFilter: queryFilter || undefined,
      pageFilter: pageFilter || undefined,
      device: device || undefined,
      country: country || undefined,
    }),
    enabled: isConnected && !!selectedSite,
    keepPreviousData: true,
  });

  if (!isConnected) return <NotConnectedMsg />;
  if (!selectedSite) return <NoSiteMsg />;

  const rows = data?.rows || [];

  // Client-side sort
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [rows, sortBy, sortDir]);

  // Summary stats
  const stats = useMemo(() => {
    if (rows.length === 0) return { keywords: 0, clicks: 0, ctr: 0, position: 0 };
    const uniqueQueries = new Set(rows.map(r => r.query));
    const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
    const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0);
    const avgPosition = rows.reduce((s, r) => s + r.position, 0) / rows.length;
    return {
      keywords: uniqueQueries.size,
      clicks: totalClicks,
      ctr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
      position: avgPosition,
    };
  }, [rows]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const handleExportCsv = () => {
    exportToCsv(sorted, [
      { label: 'Keyword', accessor: r => r.query },
      { label: 'Page', accessor: r => r.page },
      { label: 'Clicks', accessor: r => r.clicks },
      { label: 'Impressions', accessor: r => r.impressions },
      { label: 'CTR', accessor: r => (r.ctr * 100).toFixed(2) + '%' },
      { label: 'Position', accessor: r => r.position.toFixed(1) },
    ], `keywords-${dateRange.startDate}-${dateRange.endDate}.csv`);
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <div className="glow-card-filled rounded-xl px-4 py-3">
          <p className="text-xs text-muted-foreground">Keywords</p>
          <p className="text-xl font-bold text-foreground">{stats.keywords.toLocaleString()}</p>
        </div>
        <div className="glow-card-filled rounded-xl px-4 py-3">
          <p className="text-xs text-muted-foreground">Total Clicks</p>
          <p className="text-xl font-bold text-foreground">{stats.clicks.toLocaleString()}</p>
        </div>
        <div className="glow-card-filled rounded-xl px-4 py-3">
          <p className="text-xs text-muted-foreground">Avg CTR</p>
          <p className="text-xl font-bold text-foreground">{stats.ctr.toFixed(2)}%</p>
        </div>
        <div className="glow-card-filled rounded-xl px-4 py-3">
          <p className="text-xs text-muted-foreground">Avg Position</p>
          <p className="text-xl font-bold text-foreground">{stats.position.toFixed(1)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
        <input
          type="text"
          placeholder="Filter keyword..."
          value={queryFilter}
          onChange={(e) => { setQueryFilter(e.target.value); setStartRow(0); }}
          className="px-3 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm w-48"
        />
        <input
          type="text"
          placeholder="Filter page URL..."
          value={pageFilter}
          onChange={(e) => { setPageFilter(e.target.value); setStartRow(0); }}
          className="px-3 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm w-48"
        />
        <select
          value={device}
          onChange={(e) => { setDevice(e.target.value); setStartRow(0); }}
          className="px-3 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm"
        >
          <option value="">All Devices</option>
          <option value="DESKTOP">Desktop</option>
          <option value="MOBILE">Mobile</option>
          <option value="TABLET">Tablet</option>
        </select>
        <select
          value={country}
          onChange={(e) => { setCountry(e.target.value); setStartRow(0); }}
          className="px-3 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm"
        >
          <option value="">All Countries</option>
          <option value="usa">United States</option>
          <option value="gbr">United Kingdom</option>
          <option value="fra">France</option>
          <option value="deu">Germany</option>
          <option value="ind">India</option>
          <option value="can">Canada</option>
          <option value="aus">Australia</option>
        </select>
        <button
          onClick={handleExportCsv}
          disabled={rows.length === 0}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm transition-colors disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {[
                { key: 'query', label: 'Keyword', align: 'left' },
                { key: 'page', label: 'Page', align: 'left' },
                { key: 'clicks', label: 'Clicks', align: 'right' },
                { key: 'impressions', label: 'Impressions', align: 'right' },
                { key: 'ctr', label: 'CTR', align: 'right' },
                { key: 'position', label: 'Position', align: 'right' },
              ].map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`px-4 py-3 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          {isLoading ? (
            <TableSkeleton cols={6} />
          ) : (
            <tbody className="divide-y divide-border">
              {sorted.map((row, i) => (
                <tr key={i} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 text-foreground text-sm max-w-xs truncate">{row.query}</td>
                  <td className="px-4 py-2.5 text-sm max-w-xs truncate">
                    <a href={row.page} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary">
                      {row.page.replace(/^https?:\/\/[^/]+/, '')}
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-right text-foreground text-sm">{row.clicks.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-foreground text-sm">{row.impressions.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-foreground text-sm">{(row.ctr * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2.5 text-right text-foreground text-sm">{row.position.toFixed(1)}</td>
                </tr>
              ))}
              {rows.length === 0 && !isLoading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No keyword data found</td></tr>
              )}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {startRow + 1}–{startRow + rows.length} results
          {isFetching && ' (loading...)'}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStartRow(Math.max(0, startRow - ROW_LIMIT))}
            disabled={startRow === 0}
            className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm disabled:opacity-40 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => setStartRow(startRow + ROW_LIMIT)}
            disabled={rows.length < ROW_LIMIT}
            className="px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm disabled:opacity-40 transition-colors"
          >
            Load More
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PerformanceTab (Pages) — replaces DiscoveredLinksTab
// ---------------------------------------------------------------------------
function PerformanceTab({ isConnected, selectedSite, dateRange, setDateRange }) {
  const [startRow, setStartRow] = useState(0);
  const [rowLimit, setRowLimit] = useState(100);
  const [expandedRow, setExpandedRow] = useState(null);

  const { data: performanceData, isLoading, isFetching } = useQuery({
    queryKey: ['gsc-performance', selectedSite, dateRange, startRow, rowLimit],
    queryFn: () => getPerformance(selectedSite, { ...dateRange, startRow, rowLimit }),
    enabled: isConnected && !!selectedSite,
    keepPreviousData: true,
  });

  // Trend data for expanded row
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['gsc-page-trend', selectedSite, expandedRow, dateRange],
    queryFn: () => getPageTrend(selectedSite, expandedRow, dateRange),
    enabled: !!expandedRow && !!selectedSite,
  });

  if (!isConnected) return <NotConnectedMsg />;
  if (!selectedSite) return <NoSiteMsg />;

  const performance = performanceData?.performance || [];
  const trend = trendData?.trend || [];

  const handleExportCsv = () => {
    exportToCsv(performance, [
      { label: 'Page', accessor: r => r.keys[0] },
      { label: 'Clicks', accessor: r => r.clicks },
      { label: 'Impressions', accessor: r => r.impressions },
      { label: 'CTR', accessor: r => (r.ctr * 100).toFixed(2) + '%' },
      { label: 'Position', accessor: r => r.position.toFixed(1) },
    ], `pages-${dateRange.startDate}-${dateRange.endDate}.csv`);
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
          <select
            value={rowLimit}
            onChange={(e) => { setRowLimit(Number(e.target.value)); setStartRow(0); }}
            className="px-3 py-1.5 bg-muted border border-border rounded-lg text-foreground text-sm"
          >
            <option value={100}>100 rows</option>
            <option value={250}>250 rows</option>
            <option value={500}>500 rows</option>
          </select>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={performance.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm transition-colors disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
      </div>

      <p className="text-sm text-muted-foreground">
        Showing {startRow + 1}–{startRow + performance.length} pages — click a row to see daily trend
      </p>

      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Page</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Clicks</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Impressions</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">CTR</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Position</th>
            </tr>
          </thead>
          {isLoading ? (
            <TableSkeleton cols={5} />
          ) : (
            <tbody className="divide-y divide-border">
              {performance.map((row, i) => {
                const pageUrl = row.keys[0];
                const isExpanded = expandedRow === pageUrl;
                return (
                  <motion.tr key={i} layout>
                    <td colSpan={5} className="p-0">
                      <div
                        className={`grid grid-cols-[1fr_auto_auto_auto_auto] cursor-pointer hover:bg-muted/30 transition-colors ${isExpanded ? 'bg-primary/5' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : pageUrl)}
                      >
                        <div className="px-4 py-2.5 text-sm truncate max-w-md">
                          <a
                            href={pageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {pageUrl.replace(/^https?:\/\/[^/]+/, '')}
                          </a>
                        </div>
                        <div className="px-4 py-2.5 text-right text-foreground text-sm">{row.clicks.toLocaleString()}</div>
                        <div className="px-4 py-2.5 text-right text-foreground text-sm">{row.impressions.toLocaleString()}</div>
                        <div className="px-4 py-2.5 text-right text-foreground text-sm">{(row.ctr * 100).toFixed(1)}%</div>
                        <div className="px-4 py-2.5 text-right text-foreground text-sm">{row.position.toFixed(1)}</div>
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden border-t border-border/50 bg-muted/20"
                          >
                            <div className="p-4">
                              {trendLoading ? (
                                <div className="h-32 flex items-center justify-center">
                                  <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
                                </div>
                              ) : trend.length > 0 ? (
                                <div className="h-40">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={trend}>
                                      <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                        tickFormatter={(d) => d.slice(5)}
                                      />
                                      <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                                      <Tooltip
                                        contentStyle={{
                                          backgroundColor: 'hsl(var(--card))',
                                          border: '1px solid hsl(var(--border))',
                                          borderRadius: '8px',
                                          color: 'hsl(var(--foreground))',
                                        }}
                                      />
                                      <Line type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-6">No daily trend data available</p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </td>
                  </motion.tr>
                );
              })}
              {performance.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">No page data found</td></tr>
              )}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isFetching && 'Loading...'}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStartRow(Math.max(0, startRow - rowLimit))}
            disabled={startRow === 0}
            className="flex items-center gap-1 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm disabled:opacity-40 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button
            onClick={() => setStartRow(startRow + rowLimit)}
            disabled={performance.length < rowLimit}
            className="flex items-center gap-1 px-3 py-1.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm disabled:opacity-40 transition-colors"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UrlInspectionTab (Index Status)
// ---------------------------------------------------------------------------
function UrlInspectionTab({ isConnected, selectedSite, projects, selectedProject, setSelectedProject }) {
  const queryClient = useQueryClient();
  const [singleUrl, setSingleUrl] = useState('');

  // Budget query
  const { data: budgetData } = useQuery({
    queryKey: ['gsc-inspect-budget'],
    queryFn: getInspectionBudget,
    enabled: isConnected,
    refetchInterval: 30000,
  });

  // Results query
  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['gsc-inspect-results', selectedSite, selectedProject],
    queryFn: () => getInspectionResults(selectedSite, selectedProject),
    enabled: isConnected && !!selectedSite,
  });

  // Batch inspect mutation
  const batchMutation = useMutation({
    mutationFn: ({ siteUrl, projectId }) => batchInspect(siteUrl, projectId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['gsc-inspect-results']);
      queryClient.invalidateQueries(['gsc-inspect-budget']);
      toast.success(`Inspected ${data.inspected} URLs (${data.failed} failed)`);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Batch inspection failed');
    },
  });

  // Single inspect mutation
  const inspectMutation = useMutation({
    mutationFn: ({ siteUrl, inspectionUrl }) => inspectUrlApi(siteUrl, inspectionUrl),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-inspect-results']);
      queryClient.invalidateQueries(['gsc-inspect-budget']);
      setSingleUrl('');
      toast.success('URL inspected');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Inspection failed');
    },
  });

  if (!isConnected) return <NotConnectedMsg />;
  if (!selectedSite) return <NoSiteMsg />;

  const budget = budgetData || { used: 0, limit: 2000, remaining: 2000 };
  const results = resultsData?.results || [];
  const totalIndexed = resultsData?.totalIndexed || 0;
  const totalNotIndexed = resultsData?.totalNotIndexed || 0;
  const budgetPercent = (budget.used / budget.limit) * 100;

  const verdictBadge = (verdict) => {
    const colors = {
      PASS: 'text-success bg-success/15',
      FAIL: 'text-destructive bg-destructive/15',
      PARTIAL: 'text-warning bg-warning/15',
      NEUTRAL: 'text-muted-foreground bg-muted',
    };
    return colors[verdict] || colors.NEUTRAL;
  };

  return (
    <div className="space-y-4">
      {/* Budget bar */}
      <div className="glow-card-filled rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-foreground">Daily Inspection Budget</p>
          <p className="text-sm text-muted-foreground">{budget.used.toLocaleString()} / {budget.limit.toLocaleString()} used today</p>
        </div>
        <div className="w-full bg-muted rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all ${budgetPercent > 80 ? 'bg-destructive' : budgetPercent > 50 ? 'bg-warning' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, budgetPercent)}%` }}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selectedProject || ''}
          onChange={(e) => setSelectedProject(e.target.value || null)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {selectedProject && (
          <StatefulButton
            onClick={() => batchMutation.mutate({ siteUrl: selectedSite, projectId: selectedProject })}
            loading={batchMutation.isPending}
            disabled={budget.remaining === 0}
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Shield className="h-4 w-4" />
            Inspect All Backlink Targets
          </StatefulButton>
        )}
      </div>

      {/* Single URL inspection */}
      <div className="flex items-center gap-2">
        <input
          type="url"
          placeholder="Enter a URL to inspect..."
          value={singleUrl}
          onChange={(e) => setSingleUrl(e.target.value)}
          className="flex-1 px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm"
        />
        <StatefulButton
          onClick={() => inspectMutation.mutate({ siteUrl: selectedSite, inspectionUrl: singleUrl })}
          loading={inspectMutation.isPending}
          disabled={!singleUrl}
          size="sm"
        >
          Inspect
        </StatefulButton>
      </div>

      {/* Alert banner */}
      {totalNotIndexed > 0 && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-lg">
          <XCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{totalNotIndexed} page{totalNotIndexed !== 1 ? 's' : ''} receiving backlinks are NOT indexed</span>
          <span className="text-destructive/70">— zero SEO value from those backlinks</span>
        </div>
      )}

      {/* Stats row */}
      {results.length > 0 && (
        <div className="grid gap-3 grid-cols-3">
          <div className="glow-card-filled rounded-xl px-4 py-3 text-center">
            <p className="text-xs text-muted-foreground">Checked</p>
            <p className="text-lg font-bold text-foreground">{results.length}</p>
          </div>
          <div className="glow-card-filled rounded-xl px-4 py-3 text-center glow-success">
            <p className="text-xs text-muted-foreground">Indexed</p>
            <p className="text-lg font-bold text-success">{totalIndexed}</p>
          </div>
          <div className="glow-card-filled rounded-xl px-4 py-3 text-center glow-destructive">
            <p className="text-xs text-muted-foreground">Not Indexed</p>
            <p className="text-lg font-bold text-destructive">{totalNotIndexed}</p>
          </div>
        </div>
      )}

      {/* Results table */}
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">URL</th>
              <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Verdict</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Coverage</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Fetch State</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Last Crawl</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Canonical</th>
            </tr>
          </thead>
          {isLoading ? (
            <TableSkeleton cols={6} />
          ) : (
            <tbody className="divide-y divide-border">
              {results.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5 text-sm">
                    <a href={r.inspectedUrl} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary truncate block max-w-xs">
                      {r.inspectedUrl.replace(/^https?:\/\/[^/]+/, '')}
                    </a>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${verdictBadge(r.verdict)}`}>
                      {r.verdict}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">{r.coverageState || '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">{r.pageFetchState || '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">
                    {r.lastCrawlTime ? new Date(r.lastCrawlTime).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground truncate max-w-xs">
                    {r.googleCanonical ? r.googleCanonical.replace(/^https?:\/\/[^/]+/, '') : '—'}
                  </td>
                </tr>
              ))}
              {results.length === 0 && !isLoading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No inspection results yet. Inspect URLs above.</td></tr>
              )}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SitemapsTab
// ---------------------------------------------------------------------------
function SitemapsTab({ isConnected, selectedSite }) {
  const queryClient = useQueryClient();
  const [newFeedpath, setNewFeedpath] = useState('');

  const { data: sitemapsData, isLoading } = useQuery({
    queryKey: ['gsc-sitemaps', selectedSite],
    queryFn: () => getSitemaps(selectedSite),
    enabled: isConnected && !!selectedSite,
  });

  const submitMutation = useMutation({
    mutationFn: (feedpath) => submitSitemap(selectedSite, feedpath),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-sitemaps']);
      setNewFeedpath('');
      toast.success('Sitemap submitted');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to submit sitemap');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (feedpath) => deleteSitemap(selectedSite, feedpath),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-sitemaps']);
      toast.success('Sitemap deleted');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to delete sitemap');
    },
  });

  if (!isConnected) return <NotConnectedMsg />;
  if (!selectedSite) return <NoSiteMsg />;

  const sitemaps = sitemapsData?.sitemaps || [];

  return (
    <div className="space-y-4">
      {/* Submit form */}
      <div className="flex items-center gap-2">
        <input
          type="url"
          placeholder="https://example.com/sitemap.xml"
          value={newFeedpath}
          onChange={(e) => setNewFeedpath(e.target.value)}
          className="flex-1 px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm"
        />
        <StatefulButton
          onClick={() => submitMutation.mutate(newFeedpath)}
          loading={submitMutation.isPending}
          disabled={!newFeedpath}
          size="sm"
          className="inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Submit Sitemap
        </StatefulButton>
      </div>

      {isLoading ? (
        <div className="text-center py-16">
          <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading sitemaps...</p>
        </div>
      ) : sitemaps.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No sitemaps found</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Submit a sitemap URL above</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {sitemaps.map((sm, i) => {
            const hasErrors = sm.errors > 0;
            const hasWarnings = sm.warnings > 0;
            return (
              <CardSpotlight key={i} className={hasErrors ? 'border-destructive/30' : ''}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate text-sm">{sm.path}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-0.5">{sm.type || 'sitemap'}</p>
                  </div>
                  <button
                    onClick={() => deleteMutation.mutate(sm.path)}
                    disabled={deleteMutation.isPending}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                    title="Delete sitemap"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
                  {sm.lastSubmitted && (
                    <div>
                      <span className="text-muted-foreground">Submitted:</span>{' '}
                      <span className="text-foreground">{new Date(sm.lastSubmitted).toLocaleDateString()}</span>
                    </div>
                  )}
                  {sm.lastDownloaded && (
                    <div>
                      <span className="text-muted-foreground">Downloaded:</span>{' '}
                      <span className="text-foreground">{new Date(sm.lastDownloaded).toLocaleDateString()}</span>
                    </div>
                  )}
                  {sm.contents && sm.contents.map((content, ci) => (
                    <div key={ci}>
                      <span className="text-muted-foreground">{content.type}:</span>{' '}
                      <span className="text-foreground">{content.submitted?.toLocaleString()}</span>
                      {content.indexed != null && (
                        <span className="text-success ml-1">({content.indexed} indexed)</span>
                      )}
                    </div>
                  ))}
                </div>

                {(hasErrors || hasWarnings) && (
                  <div className="mt-2 flex items-center gap-2">
                    {hasErrors && (
                      <span className="text-xs text-destructive bg-destructive/15 px-2 py-0.5 rounded-full">
                        {sm.errors} error{sm.errors !== 1 ? 's' : ''}
                      </span>
                    )}
                    {hasWarnings && (
                      <span className="text-xs text-warning bg-warning/15 px-2 py-0.5 rounded-full">
                        {sm.warnings} warning{sm.warnings !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </CardSpotlight>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab - with project linking per site card
// ---------------------------------------------------------------------------
function OverviewTab({
  isConnected,
  sites,
  projects,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
  selectedSite,
  setSelectedSite,
}) {
  const queryClient = useQueryClient();

  // Mutation: link a GSC site to a project
  const linkMutation = useMutation({
    mutationFn: ({ siteUrl, projectId }) => linkSiteToProject(siteUrl, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-sites']);
      toast.success('Site linked to project');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to link site');
    },
  });

  // Mutation: unlink a GSC site from a project
  const unlinkMutation = useMutation({
    mutationFn: (projectId) => unlinkSite(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-sites']);
      toast.success('Site unlinked from project');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to unlink site');
    },
  });

  // Build a lookup of projectId -> project name for display
  const projectNameMap = useMemo(() => {
    const map = {};
    (projects || []).forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [projects]);

  if (!isConnected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
          <Search className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Connect Google Search Console
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">
          Connect your GSC account to discover backlinks Google has found, identify untracked links,
          and manage your disavow file.
        </p>
        <StatefulButton
          onClick={onConnect}
          loading={isConnecting}
          className="inline-flex items-center gap-2"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Connect with Google
        </StatefulButton>

        <div className="mt-12 grid gap-4 md:grid-cols-3 max-w-3xl mx-auto text-left">
          <FeatureCard
            icon={<Link2 className="h-5 w-5 text-primary" />}
            title="Discover Backlinks"
            description="See all backlinks Google has found pointing to your site"
          />
          <FeatureCard
            icon={<Search className="h-5 w-5 text-success" />}
            title="Find Untracked"
            description="Identify backlinks you're not monitoring"
          />
          <FeatureCard
            icon={<AlertTriangle className="h-5 w-5 text-warning" />}
            title="Manage Disavow"
            description="Build and submit disavow files directly"
          />
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Connected Sites"
          value={sites.length}
          icon={<Globe className="h-5 w-5" />}
          color="blue"
        />
        <StatCard
          title="Verified"
          value={sites.filter(s => s.permissionLevel !== 'siteUnverifiedUser').length}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
        />
        <StatCard
          title="Selected Site"
          value={selectedSite ? '1' : '0'}
          icon={<Search className="h-5 w-5" />}
          color="amber"
        />
        <div className="rounded-xl border border-border bg-card p-4">
          <button
            onClick={onDisconnect}
            disabled={isDisconnecting}
            className="w-full h-full flex flex-col items-center justify-center text-destructive hover:text-destructive/80 transition-colors"
          >
            <XCircle className="h-6 w-6 mb-2" />
            <span className="text-sm font-medium">
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </span>
          </button>
        </div>
      </div>

      {/* Site Selector */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Select a Site</h3>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => {
            const linkedProjectName = site.linkedProjectId
              ? projectNameMap[site.linkedProjectId]
              : null;

            return (
              <CardSpotlight
                key={site.siteUrl}
                className={`cursor-pointer transition-all ${
                  selectedSite === site.siteUrl
                    ? 'ring-2 ring-primary'
                    : 'hover:border-muted-foreground'
                }`}
                onClick={() => setSelectedSite(site.siteUrl)}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedSite === site.siteUrl ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    <Globe className={`h-5 w-5 ${
                      selectedSite === site.siteUrl ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{site.siteUrl}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {site.permissionLevel?.replace('site', '')}
                    </p>
                  </div>
                  {selectedSite === site.siteUrl && (
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>

                {/* Project linking section */}
                <div
                  className="mt-3 pt-3 border-t border-border/50"
                  onClick={(e) => e.stopPropagation()}
                >
                  {linkedProjectName ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <LinkIcon className="h-3.5 w-3.5 text-success flex-shrink-0" />
                        <span className="text-xs text-success truncate">
                          {linkedProjectName}
                        </span>
                      </div>
                      <button
                        onClick={() => unlinkMutation.mutate(site.linkedProjectId)}
                        disabled={unlinkMutation.isPending}
                        className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 transition-colors flex-shrink-0"
                      >
                        <Unlink className="h-3 w-3" />
                        {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <LinkIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            linkMutation.mutate({
                              siteUrl: site.siteUrl,
                              projectId: e.target.value,
                            });
                          }
                        }}
                        disabled={linkMutation.isPending}
                        className="flex-1 min-w-0 text-xs px-2 py-1 bg-muted border border-border/50 rounded text-foreground"
                      >
                        <option value="">Link to project...</option>
                        {(projects || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </CardSpotlight>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UntrackedTab - with bulk import and per-row import
// ---------------------------------------------------------------------------
function UntrackedTab({ isConnected, selectedSite, projects, selectedProject, setSelectedProject }) {
  const queryClient = useQueryClient();
  const [selectedUrls, setSelectedUrls] = useState(new Set());

  const { data: untrackedData, isLoading, refetch } = useQuery({
    queryKey: ['gsc-untracked', selectedSite, selectedProject],
    queryFn: () => discoverUntracked(selectedSite, selectedProject),
    enabled: isConnected && !!selectedSite,
  });

  // Bulk import mutation
  const importMutation = useMutation({
    mutationFn: ({ projectId, pages }) => importDiscovered(projectId, pages),
    onSuccess: (data) => {
      const count = data?.imported ?? selectedUrls.size;
      queryClient.invalidateQueries(['gsc-untracked']);
      queryClient.invalidateQueries(['backlinks']);
      setSelectedUrls(new Set());
      toast.success(`Imported ${count} link${count !== 1 ? 's' : ''} into project`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to import links');
    },
  });

  // Single-row import mutation
  const singleImportMutation = useMutation({
    mutationFn: ({ projectId, pages }) => importDiscovered(projectId, pages),
    onSuccess: () => {
      queryClient.invalidateQueries(['gsc-untracked']);
      queryClient.invalidateQueries(['backlinks']);
      toast.success('Link imported');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to import link');
    },
  });

  if (!isConnected) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Connect Google Search Console to find untracked backlinks</p>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Select a site from the Overview tab first</p>
      </div>
    );
  }

  const untracked = untrackedData?.untracked || [];
  const displayedRows = untracked.slice(0, 50);
  const allSelected = displayedRows.length > 0 && displayedRows.every((row) => selectedUrls.has(row.url));

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedUrls(new Set());
    } else {
      setSelectedUrls(new Set(displayedRows.map((row) => row.url)));
    }
  };

  const handleToggleRow = (url) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const handleBulkImport = () => {
    if (!selectedProject) {
      toast.error('Select a project before importing');
      return;
    }
    if (selectedUrls.size === 0) {
      toast.error('Select at least one link to import');
      return;
    }
    const pages = untracked.filter((row) => selectedUrls.has(row.url));
    importMutation.mutate({ projectId: selectedProject, pages });
  };

  const handleSingleImport = (row) => {
    if (!selectedProject) {
      toast.error('Select a project before importing');
      return;
    }
    singleImportMutation.mutate({ projectId: selectedProject, pages: [row] });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value || null)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground">
            {untracked.length} untracked links found
          </span>
        </div>

        <div className="flex items-center gap-2">
          {selectedUrls.size > 0 && (
            <StatefulButton
              onClick={handleBulkImport}
              loading={importMutation.isPending}
              disabled={!selectedProject}
              size="sm"
              className="inline-flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Import Selected ({selectedUrls.size})
            </StatefulButton>
          )}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Project required hint */}
      {!selectedProject && untracked.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-warning bg-warning/10 px-3 py-2 rounded-lg">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          Select a project above to enable importing links
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-16">
          <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Comparing with your tracked backlinks...</p>
        </div>
      ) : untracked.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-12 w-12 text-success mx-auto mb-4" />
          <p className="text-muted-foreground">All links are tracked!</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-10 px-4 py-3">
                  <button
                    onClick={handleToggleAll}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title={allSelected ? 'Deselect all' : 'Select all'}
                  >
                    {allSelected ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">URL</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Clicks</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Impressions</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {displayedRows.map((row, i) => {
                const isSelected = selectedUrls.has(row.url);
                return (
                  <tr
                    key={i}
                    className={`hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                  >
                    <td className="w-10 px-4 py-3">
                      <button
                        onClick={() => handleToggleRow(row.url)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="h-4 w-4 text-primary" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground hover:text-primary truncate block max-w-md"
                      >
                        {row.url}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground">{row.clicks}</td>
                    <td className="px-4 py-3 text-right text-foreground">{row.impressions}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleSingleImport(row)}
                        disabled={!selectedProject || singleImportMutation.isPending}
                        className="text-primary hover:text-primary/80 text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title={selectedProject ? 'Import this link' : 'Select a project first'}
                      >
                        + Add
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DisavowTab - with notes textarea and history panel
// ---------------------------------------------------------------------------
function DisavowTab({ projects, selectedProject, setSelectedProject }) {
  const queryClient = useQueryClient();
  const [disavowContent, setDisavowContent] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  // Disavow suggestions query
  const { data: suggestionsData, isLoading } = useQuery({
    queryKey: ['disavow-suggestions', selectedProject],
    queryFn: () => getDisavowSuggestions(selectedProject, 50),
  });

  // Disavow history query
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['disavow-history', selectedProject],
    queryFn: () => getDisavowHistory(selectedProject),
  });

  // Generate mutation - passes projectId and notes
  const generateMutation = useMutation({
    mutationFn: ({ links, projectId, notes: noteText }) =>
      generateDisavow(links, projectId, noteText),
    onSuccess: (data) => {
      setDisavowContent(data.content);
      queryClient.invalidateQueries(['disavow-history']);
      toast.success(`Generated disavow file with ${data.linkCount} entries`);
    },
    onError: () => {
      toast.error('Failed to generate disavow file');
    },
  });

  const suggestions = suggestionsData?.suggestions || [];
  const byDomain = suggestionsData?.byDomain || {};
  const history = historyData?.history || [];

  const handleGenerateDisavow = () => {
    const links = Object.keys(byDomain).map(domain => ({ domain }));
    generateMutation.mutate({
      links,
      projectId: selectedProject || null,
      notes: notes.trim() || null,
    });
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(disavowContent);
    toast.success('Copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([disavowContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `disavow-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleHistoryExpand = (id) => {
    setExpandedHistoryId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value || null)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground">
            {suggestions.length} links suggested for disavow
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerateDisavow}
            disabled={Object.keys(byDomain).length === 0 || generateMutation.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm disabled:opacity-50 transition-colors"
          >
            <FileText className="h-4 w-4" />
            {generateMutation.isPending ? 'Generating...' : 'Generate Disavow'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Suggestions */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Disavow Suggestions
            <span className="text-sm font-normal text-muted-foreground ml-2">
              (high spam score or removed)
            </span>
          </h3>

          {isLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 text-muted-foreground animate-spin mx-auto" />
            </div>
          ) : Object.keys(byDomain).length === 0 ? (
            <CardSpotlight className="text-center py-8">
              <CheckCircle className="h-10 w-10 text-success mx-auto mb-3" />
              <p className="text-muted-foreground">No toxic links detected</p>
            </CardSpotlight>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Object.entries(byDomain).map(([domain, links]) => (
                <CardSpotlight key={domain} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">{domain}</p>
                      <p className="text-xs text-muted-foreground">{links.length} links</p>
                    </div>
                    <span className="text-xs text-destructive bg-destructive/20 px-2 py-1 rounded">
                      Toxic
                    </span>
                  </div>
                </CardSpotlight>
              ))}
            </div>
          )}

          {/* Notes textarea before generating */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              <StickyNote className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this disavow generation (e.g., reason for disavow, review date)..."
              className="w-full h-20 px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm resize-none placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            />
          </div>
        </div>

        {/* Generated File */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Disavow File</h3>

          {disavowContent ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  onClick={handleCopyContent}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm transition-colors"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
              <textarea
                value={disavowContent}
                readOnly
                className="w-full h-64 px-4 py-3 bg-muted border border-border rounded-lg text-foreground font-mono text-sm resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Upload this file at{' '}
                <a
                  href="https://search.google.com/search-console/disavow-links"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80"
                >
                  Google's Disavow Tool
                </a>
              </p>
            </div>
          ) : (
            <CardSpotlight className="text-center py-12">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No disavow file generated yet</p>
              <p className="text-xs text-muted-foreground">
                Click "Generate Disavow" to create a file from suggestions
              </p>
            </CardSpotlight>
          )}
        </div>
      </div>

      {/* History Panel */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          <Clock className="h-5 w-5 inline mr-2 -mt-0.5" />
          Disavow History
        </h3>

        {historyLoading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-6 w-6 text-muted-foreground animate-spin mx-auto" />
          </div>
        ) : history.length === 0 ? (
          <CardSpotlight className="text-center py-8">
            <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No disavow files generated yet</p>
          </CardSpotlight>
        ) : (
          <div className="space-y-2">
            {history.map((entry) => {
              const isExpanded = expandedHistoryId === entry.id;
              const createdDate = entry.createdAt
                ? new Date(entry.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Unknown date';

              return (
                <div key={entry.id} className="glow-card-filled p-0 overflow-hidden">
                  <button
                    onClick={() => toggleHistoryExpand(entry.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0 flex items-center gap-4 flex-wrap">
                      <span className="text-sm font-medium text-foreground">
                        {createdDate}
                      </span>

                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {entry.domainCount != null && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {entry.domainCount} domain{entry.domainCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {entry.urlCount != null && (
                          <span className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {entry.urlCount} URL{entry.urlCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {entry.notes && (
                        <span className="text-xs text-muted-foreground/80 truncate max-w-xs">
                          {entry.notes}
                        </span>
                      )}
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-1 border-t border-border/50">
                          {entry.notes && (
                            <div className="mb-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                              <p className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2">
                                {entry.notes}
                              </p>
                            </div>
                          )}
                          {entry.content ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-medium text-muted-foreground">File Content</p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(entry.content);
                                    toast.success('Copied to clipboard!');
                                  }}
                                  className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                                >
                                  <Copy className="h-3 w-3" />
                                  Copy
                                </button>
                              </div>
                              <pre className="text-xs text-foreground/80 font-mono bg-muted/50 rounded-lg px-3 py-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {entry.content}
                              </pre>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">
                              File content not available
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeatureCard helper
// ---------------------------------------------------------------------------
function FeatureCard({ icon, title, description }) {
  return (
    <CardSpotlight className="p-4">
      <div className="p-2 rounded-lg bg-muted w-fit mb-3">{icon}</div>
      <h4 className="font-medium text-foreground mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </CardSpotlight>
  );
}
